import React from 'react';
import { useLocation } from 'react-router-dom';
import { currentHistoryIndex } from '@/utils';

/**
 * A product's origin (bos-app#137): the history entry that opened its details
 * page, normally the filtered Products list. It travels in router state, as
 * the entry's in-app history index:
 *
 * - the list's product link and its New product action record it;
 * - the create form passes it on when it replaces itself with the new
 *   product's details page;
 * - the details page keeps it on every change to its own address (the branch
 *   picker replaces the entry) and passes it on to the edit form.
 *
 * After a Delete or "Archive instead" the edit form goes back through history
 * to it, which restores the list's address exactly and leaves nothing of the
 * product on the back stack.
 */
export type ProductOriginState = { productOrigin: number };

/** The origin `state` carries, or `null` when it carries none. */
const readProductOrigin = (state: unknown): number | null => {
    if (typeof state !== 'object' || state === null) {
        return null;
    }
    const origin = (state as Partial<ProductOriginState>).productOrigin;
    return typeof origin === 'number' && Number.isInteger(origin) && origin >= 0
        ? origin
        : null;
};

const originStateAt = (index: number | null): ProductOriginState | undefined =>
    index === null ? undefined : { productOrigin: index };

/**
 * Router state that names the current entry as the origin of a product the
 * page opens, by a link or an action; none for an entry react-router did not
 * create. `useLocation` renders the page again whenever the address moves, as
 * a filter or a page change adds an entry, so the state always names the
 * entry the page is on.
 */
export const useProductOriginState = (): ProductOriginState | undefined => {
    useLocation();
    const index = currentHistoryIndex();
    return React.useMemo(() => originStateAt(index), [index]);
};

/** The origin `state` carries, to hand on to the product's next page. */
export const keepProductOrigin = (
    state: unknown,
): ProductOriginState | undefined => {
    return originStateAt(readProductOrigin(state));
};

/**
 * How far back the origin `state` carries lies, or `null` when there is none
 * to go back to: none was carried (a deep link, a new tab, a lost state), or
 * the origin isn't behind the current entry.
 */
export const stepsBackToOrigin = (state: unknown): number | null => {
    const origin = readProductOrigin(state);
    const current = currentHistoryIndex();
    if (origin === null || current === null || origin >= current) {
        return null;
    }
    return current - origin;
};
